import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ShieldLogo } from "../components/landing/Logo";
import { Lock, Loader2, AlertCircle, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

// ── Social stub button ────────────────────────────────────────────────
const SocialButton = ({ icon, label, comingSoon = false, onClick }) => (
  <button
    type="button"
    disabled={comingSoon}
    onClick={!comingSoon ? onClick : undefined}
    className="w-full relative flex items-center justify-center gap-3 h-11 rounded-lg border transition-all text-sm font-medium"
    style={{
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.1)",
      color: comingSoon ? "#4b5563" : "#9ca3af",
      cursor: comingSoon ? "not-allowed" : "pointer",
    }}
    title={comingSoon ? "Coming soon" : undefined}
    data-testid={`social-login-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {icon}
    <span>{label}</span>
    {comingSoon && (
      <span
        className="absolute right-3 text-[10px] text-gray-700 font-mono uppercase tracking-wider"
      >
        Soon
      </span>
    )}
  </button>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.41c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.39-1.32 2.76-2.54 3.98zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

export default function AdminLogin() {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectTo = searchParams.get("redirect") || "/admin/command-center";

  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo, { replace: true });
  }, [isAuthenticated, navigate, redirectTo]);

  if (isAuthenticated) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    const result = await login(password);
    if (result.success) {
      navigate(redirectTo, { replace: true });
    } else {
      // Normalise any backend error to a friendly message
      const raw = result.error || "";
      setError(
        raw.toLowerCase().includes("invalid") || raw.toLowerCase().includes("401")
          ? "Invalid credentials. Please try again."
          : raw || "Something went wrong. Please try again."
      );
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#040F49] flex items-center justify-center p-6">
      <div className="noise-overlay" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass-card rounded-xl p-8 border border-gold/20">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <ShieldLogo size="lg" />
            </div>
            <h1 className="font-inter text-2xl font-bold text-white mb-2">Admin Access</h1>
            <p className="text-sm text-slate-400">
              Enter your password to access the Command Center
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Password field with eye toggle */}
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 z-10" />
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                className="pl-10 pr-11 bg-indigo-700/30 border-gold/20 text-white h-12"
                data-testid="admin-password-input"
                autoFocus
              />
              <button
                type="button"
                data-testid="toggle-password-visibility"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* Error message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30"
                  data-testid="login-error-message"
                >
                  <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                  <p className="text-sm text-red-400">{error}</p>
                </motion.div>
              )}
            </AnimatePresence>

            <Button
              type="submit"
              disabled={isLoading || !password}
              className="w-full h-12 bg-gold hover:bg-gold/80 text-indigo-700 font-bold"
              data-testid="admin-login-button"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <><Lock className="w-4 h-4 mr-2" /> Access Command Center</>
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/8" />
            <span className="text-[11px] text-gray-600 font-mono uppercase tracking-wider">or sign in with</span>
            <div className="flex-1 h-px bg-white/8" />
          </div>

          {/* Social stubs */}
          <div className="space-y-3">
            <SocialButton
              icon={<GoogleIcon />}
              label="Continue with Google"
              comingSoon={false}
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/admin/auth-callback';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
            />
            <SocialButton
              icon={<AppleIcon />}
              label="Continue with Apple"
              comingSoon={true}
            />
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gold/10 text-center">
            <p className="text-xs text-slate-500">
              Protected area for Argus Xenia administrators only.
            </p>
            <a href="/" className="text-xs text-gold hover:text-gold/80 mt-2 inline-block">
              ← Back to Landing Page
            </a>
          </div>
        </div>

        <div className="mt-4 text-center">
          <span className="inline-flex items-center gap-1 text-[10px] text-slate-600">
            <Lock className="w-3 h-3" />
            Secured with JWT Authentication.
          </span>
        </div>
      </motion.div>
    </div>
  );
}
